import { TemplateResult, html, CSSResultGroup } from 'lit';
import { property, queryAssignedElements } from 'lit/decorators.js';
import { BlocksSegmentItemChange } from '../../events/blocks-segment-item-change';
import { BlocksElement } from '../BlocksElement';
import { BlocksSegmentedControlItem } from '../segmented-control-item/segmented-control-item';
import styles from './segmented-control.styles';

/**
 * @slot default - This displays sequence of segmented control item given
 * @fires bl-segment-item-change - emitted when user changes from one segmented-item to other
 * @csspart base - The component's base wrapper.
 * @csspart header - The component's header wrapper.
 * @csspart list - The component's list.
 * @csspart part-default - The component's default slot.
 */
export class BlocksSegmentedControl extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * 1) Sets the initial active segment control item (e.g. 0 sets the first segment control item,  1 sets the second control item, etc.)
   */
  @property({ type: Number })
  activeIndex: number | undefined;

  @queryAssignedElements({ flatten: true }) private segmentItemList!: BlocksSegmentedControlItem[];

  /**
   * Active Segment item
   */
  private activeSegmentItem?: BlocksSegmentedControlItem;

  private handleSegmentItemSelect(e: CustomEvent) {
    if (this.activeSegmentItem) {
      this.activeSegmentItem.isActive = false;
    }
    this.activeSegmentItem = e.target as BlocksSegmentedControlItem;
    const activeIndex = this.segmentItemList.findIndex((segment) => segment === this.activeSegmentItem);
    this.activeIndex = activeIndex;
    this.dispatchSegmentChangeEvent();
  }

  /**
   * @fires SegmentItemChange
   */
  private dispatchSegmentChangeEvent() {
    this.dispatch({
      eventName: 'bl-segment-item-change',
      detailObj: { activeIndex: this.activeIndex }
    }) as BlocksSegmentItemChange;
  }

  /**
   * 1. Deactivate the previous active item to maintain single selection
   * 2. Activate the newly selected item based on activeIndex
   */

  private handleIsActive = () => {
    if (this.activeIndex !== undefined && this.activeIndex >= 0 && this.activeIndex < this.segmentItemList?.length) {
      if (!this.segmentItemList[this.activeIndex].disabled && !this.segmentItemList[this.activeIndex].isLoading) {
        if (this.activeSegmentItem) {
          this.activeSegmentItem.isActive = false; /**1 */
        }

        this.activeSegmentItem = this.segmentItemList[this.activeIndex];
        this.activeSegmentItem.isActive = true; /** 2 */
      }
    }
  };

  protected updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('activeIndex')) {
      this.handleIsActive();
    }
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-segmented-control', {});

    return html`
      <div part="base" class="${componentClassNames}">
        <header part="header" class="cat-c-segmented-control__header">
          <div part="list" class="cat-c-segmented-control__list" role="group">
            <slot part="part-default" @slotchange=${this.handleIsActive} @bl-segment-item-select=${this.handleSegmentItemSelect}></slot>
          </div>
        </header>
      </div>
    `;
  }
}

if (customElements.get('cat-segmented-control') === undefined) {
  customElements.define('cat-segmented-control', BlocksSegmentedControl);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-segmented-control': BlocksSegmentedControl;
  }
}
